import { useCallback, useState } from 'react';
import { Button, ButtonVariants, IconButton } from '../../../Button';
import { Stack } from '../../../Core';
import { FormGroup, Input } from '../../../Form';
import { IconName } from '../../../Icons';
import { Text } from '../../../Text';
import { useMobileModal } from '../../MobileModalSheet';
import { MobileModalSheetActionsContainer } from '../../styles';
import { OverflowModalWrapper } from '../OverflowModal/styles';
import type { TabBarItem } from '../types';
import { DeleteTabConfirmModal, getRemoveTabTitle } from './DeleteTabConfirmModal';

interface TabActionsModalProps {
  item: TabBarItem;
  onRenameItem?: (item: TabBarItem, label: string) => void;
  onDeleteItem?: (item: TabBarItem) => void;
  shouldConfirmDeleteItem?: boolean;
}

export function TabActionsModal({
  item,
  onRenameItem,
  onDeleteItem,
  shouldConfirmDeleteItem = true,
}: TabActionsModalProps) {
  const { closeModal, popModal, pushModal } = useMobileModal();
  const [label, setLabel] = useState(item.label ?? '');
  const trimmedLabel = label.trim();
  const canRename =
    item.isEditable === true && onRenameItem != null && trimmedLabel.length > 0 && trimmedLabel !== item.label;
  const canDelete = item.isRemovable === true && onDeleteItem != null;

  const handleRename = useCallback(() => {
    if (!canRename) {
      return;
    }

    onRenameItem(item, trimmedLabel);
    closeModal();
  }, [canRename, closeModal, item, onRenameItem, trimmedLabel]);

  const handleDelete = useCallback(() => {
    if (!canDelete) {
      return;
    }

    if (!shouldConfirmDeleteItem) {
      onDeleteItem(item);
      closeModal();
      return;
    }

    pushModal({
      content: <DeleteTabConfirmModal item={item} onDeleteItem={onDeleteItem} />,
      title: getRemoveTabTitle(item),
      useSheetContent: true,
      bottomSheet: true,
      bottomSheetHeight: 240,
    });
  }, [canDelete, closeModal, item, onDeleteItem, pushModal, shouldConfirmDeleteItem]);

  return (
    <OverflowModalWrapper>
      {item.isEditable === true && (
        <Stack gap="spacing12">
          <Text color="colorTextImportant">Change the label for the &quot;{item.label}&quot; tab</Text>
          <FormGroup label="Tab label" error={!trimmedLabel ? 'Label is required' : ''}>
            <Input
              autoFocus
              invalid={!trimmedLabel}
              placeholder="Enter new label"
              value={label}
              onChange={event => setLabel(event.target.value)}
              onFocus={event => event.target.select()}
              data-testid="mobile-sub-tab-rename-input"
            />
          </FormGroup>
        </Stack>
      )}
      <MobileModalSheetActionsContainer mt="auto">
        {canDelete && (
          <IconButton
            round
            variant={ButtonVariants.Negative}
            icon={IconName.Trash}
            aria-label="Delete tab"
            onClick={handleDelete}
            data-testid="mobile-sub-tab-delete-button"
          />
        )}
        <Button
          flex={1}
          pill
          variant={ButtonVariants.Default}
          onClick={popModal}
          data-testid="mobile-sub-tab-actions-cancel-button"
        >
          Cancel
        </Button>
        {item.isEditable === true && (
          <Button
            flex={1}
            pill
            variant={ButtonVariants.Primary}
            disabled={!canRename}
            onClick={handleRename}
            data-testid="mobile-sub-tab-rename-button"
          >
            Update tab
          </Button>
        )}
      </MobileModalSheetActionsContainer>
    </OverflowModalWrapper>
  );
}
